import React, { useEffect, useRef, useState } from "react";

/**
 * Overlay por celda con SPLIT 50/50:
 *  - Mitad IZQUIERDA = Temperatura (azul/amarillo)
 *  - Mitad DERECHA   = Humedad (verde/rojo)
 *  - Lee posiciones reales de .tile-abs (autoFromTiles=true) para calzar exacto.
 */
export default function ShelfHeatmap({
  rects = [],
  deviceIds = [],
  // readings[id] = { temp:{value,ts}, hum:{value,ts} }
  readings = {},
  limitsTempById = {},
  limitsHumById = {},
  defaultTempLimits = { minOk: 20, maxOk: 28 },
  defaultHumLimits = { minOk: 40, maxOk: 60 },

  // Colores T°
  okColorTemp = "#2563eb",    // azul
  warnColorTemp = "#f59e0b",  // amarillo
  // Colores Humedad
  okColorHum = "#22c55e",     // verde
  warnColorHum = "#ef4444",   // rojo

  cellOpacity = 0.6,
  // Ajuste fino por lado (en % del tamaño de la celda)
  cellPadding = { top: 6, right: 2, bottom: 6, left: 2 },
  // Separación entre mitades (en % del ancho). 0 = sin línea blanca al centro
  splitGapPct = 0,
  cellBorderRadius = 2,        // px

  autoFromTiles = true,
  tileSelector = ".tile-abs",

  staleMinutes = 10,
}) {
  const overlayRef = useRef(null);
  const [tileRects, setTileRects] = useState([]);

  const isFresh = (ts) =>
    !!ts && Date.now() - new Date(ts).getTime() <= staleMinutes * 60 * 1000;

  const hexToRgb = (hex) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return m ? { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) } : { r: 0, g: 0, b: 0 };
  };
  const rgbaFromHex = (hex, a) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r},${g},${b},${a})`;
  };

  // Tomar bounding boxes desde `.tile-abs` (para que calce exacto con la estantería)
  useEffect(() => {
    if (!autoFromTiles) return;

    const overlay = overlayRef.current;
    if (!overlay) return;

    const container = overlay.parentElement; // .shelf-abs
    if (!container) return;

    const compute = () => {
      const cont = container.getBoundingClientRect();
      const nodes = Array.from(container.querySelectorAll(tileSelector));
      if (!cont.width || !cont.height || nodes.length === 0) {
        setTileRects([]);
        return;
      }
      const arr = nodes.map((n) => {
        const r = n.getBoundingClientRect();
        const left = ((r.left - cont.left) / cont.width) * 100;
        const top = ((r.top - cont.top) / cont.height) * 100;
        const width = (r.width / cont.width) * 100;
        const height = (r.height / cont.height) * 100;
        return { left, top, width, height };
      });
      setTileRects(arr);
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(container);

    const mo = new MutationObserver(() => compute());
    mo.observe(container, { childList: true, subtree: true, attributes: true });

    const t1 = setTimeout(compute, 0);
    const t2 = setTimeout(compute, 250);

    return () => {
      ro.disconnect();
      mo.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [autoFromTiles, tileSelector]);

  const resolvePad = (i, rect) => {
    const base = { top: 4, right: 1.2, bottom: 4, left: 1.2 };
    if (typeof cellPadding === "function") return { ...base, ...(cellPadding(i, deviceIds[i], rect) || {}) };
    if (Array.isArray(cellPadding)) return { ...base, ...(cellPadding[i] || {}) };
    return { ...base, ...(cellPadding || {}) };
  };

  const sourceRects = autoFromTiles && tileRects.length ? tileRects : rects;

  return (
    <div ref={overlayRef} className="shelf-heatmap">
      {sourceRects.map((r, i) => {
        const id = deviceIds[i];
        const rd = id ? readings[id] : null;

        const tVal = rd?.temp?.value;
        const tTs  = rd?.temp?.ts;
        const hVal = rd?.hum?.value;
        const hTs  = rd?.hum?.ts;

        const tFresh = isFresh(tTs) && Number.isFinite(Number(tVal));
        const hFresh = isFresh(hTs) && Number.isFinite(Number(hVal));

        const tLim = (id && limitsTempById[id]) || defaultTempLimits;
        const hLim = (id && limitsHumById[id])  || defaultHumLimits;

        const pad = resolvePad(i, r);
        const left   = r.left   + (r.width  * pad.left  / 100);
        const top    = r.top    + (r.height * pad.top   / 100);
        const width  = r.width  * (1 - (pad.left + pad.right) / 100);
        const height = r.height * (1 - (pad.top  + pad.bottom) / 100);

        const gap = width * ((Number(splitGapPct) || 0) / 100);
        const half = (width - gap) / 2;

        // Temperatura
        let bgTemp = "transparent";
        if (tFresh) {
          const ok = Number(tVal) >= tLim.minOk && Number(tVal) <= tLim.maxOk;
          bgTemp = rgbaFromHex(ok ? okColorTemp : warnColorTemp, cellOpacity);
        }
        // Humedad
        let bgHum = "transparent";
        if (hFresh) {
          const ok = Number(hVal) >= hLim.minOk && Number(hVal) <= hLim.maxOk;
          bgHum = rgbaFromHex(ok ? okColorHum : warnColorHum, cellOpacity);
        }

        return (
          <React.Fragment key={`cell-${i}`}>
            {/* Mitad izquierda: TEMPERATURA */}
            <div
              style={{
                position: "absolute",
                left: `${left}%`,
                top: `${top}%`,
                width: `${half}%`,
                height: `${height}%`,
                background: bgTemp,
                borderTopLeftRadius: `${cellBorderRadius}px`,
                borderBottomLeftRadius: `${cellBorderRadius}px`,
                pointerEvents: "none",
                mixBlendMode: "multiply",
                transition: "all 200ms ease-out",
              }}
            />
            {/* Mitad derecha: HUMEDAD */}
            <div
              style={{
                position: "absolute",
                left: `${left + half + gap}%`,
                top: `${top}%`,
                width: `${half}%`,
                height: `${height}%`,
                background: bgHum,
                borderTopRightRadius: `${cellBorderRadius}px`,
                borderBottomRightRadius: `${cellBorderRadius}px`,
                pointerEvents: "none",
                mixBlendMode: "multiply",
                transition: "all 200ms ease-out",
              }}
            />
          </React.Fragment>
        );
      })}
    </div>
  );
}
